import React from "react";
import { commitMutation, graphql } from "react-relay";
import { type RouteComponentProps, withRouter } from "react-router-dom";

import { faInfoCircle, faQuestionCircle, faUserPlus } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faSpinnerThird } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faUsers } from "@awesome.me/kit-4c83d50d0d/icons/kit/custom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { find, identity, pick, slice, unionBy, xorBy } from "lodash";
import moment from "moment";

import { KeyboardNavigation } from "~/common";
import recaptcha from "~/lib/recaptcha";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { reduceEdgesToNodes, setTooltip } from "~/lib/utils";
import { environment } from "~/models";
import { Button } from "~/ui";
import Avatar from "~/ui/legacy/Avatar";
import { Modal } from "~/ui/legacyFellowIndex";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";
import Selector from "~/ui/selectors/Selector";
import toast from "~/ui/toast";

import ContactSearch, { type SimpleContact } from "./ContactSearch";

import type { GenericInviteQuery } from "~/__generated__/GenericInviteQuery.graphql";
import type {
	GenericInviteUsersMutation,
	GenericInviteUsersMutation$data,
	InviteUserInput,
} from "~/__generated__/GenericInviteUsersMutation.graphql";

import styles from "./css/GenericInvite.module.css";

export const InviteUsersMutation = graphql`
	mutation GenericInviteUsersMutation($invite: InviteUsersMutationInput!) {
		inviteUsers(input: $invite) {
			invitees {
				errors {
					field
					message
				}
				user {
					id
					email
					fullName
					isConfigured
				}
			}
		}
	}
`;

export type Contact = SimpleContact & { sendInviteEmail: boolean; relationship: Relationship };
export type Step = "contacts" | "relationships";

export type Relationship = NonNullable<InviteUserInput["relationship"]> | undefined;
export type Invitees = NonNullable<GenericInviteUsersMutation$data["inviteUsers"]>["invitees"];

const CONTACTS_PER_PAGE = 4;

interface InviteContactProps {
	contact: Contact;
	onRelationshipChange: (contact: Contact, value: Relationship) => void;
}

const relationshipOptions: { label: string; value: NonNullable<Relationship> }[] = [
	{ label: "Direct report", value: "SUBORDINATE" },
	{ label: "Teammate", value: "TEAMMATE" },
	{ label: "My manager", value: "MANAGER" },
];

export function InviteContact({ contact, onRelationshipChange }: InviteContactProps) {
	const onChange = React.useCallback(
		(newValue: { label: string; value: Relationship } | null) => {
			return onRelationshipChange(contact, newValue?.value);
		},
		[contact, onRelationshipChange],
	);

	return (
		<div className={styles.contact}>
			<div className={styles.contactInfo}>
				<Avatar user={contact} link={false} />
				<div className={styles.contactName}>
					{contact.fullName}
					<div className={styles.contactEmail}>{contact.email}</div>
				</div>
			</div>
			{!contact.user?.isConfigured &&
				(contact.isGroup ? (
					<div className={styles.groupRelationship}>
						Teammate
						<div
							{...setTooltip(
								"All users in this group email will be invited as teammates",
								"top center",
								true,
								false,
								"200px",
							)}
						>
							<FontAwesomeIcon icon={faInfoCircle} />
						</div>
					</div>
				) : (
					<Selector
						width="10em"
						placeholder="Select an option"
						isSearchable={false}
						value={relationshipOptions.find(({ value }) => value === contact.relationship)}
						options={relationshipOptions}
						onChange={onChange}
					/>
				))}
		</div>
	);
}

enum SuggestionReason {
	RECURRING_ONE_ON_ONE = "RECURRING_ONE_ON_ONE",
	UPCOMING_ONE_ON_ONE = "UPCOMING_ONE_ON_ONE",
	RECURRING_MEETING = "RECURRING_MEETING",
	UPCOMING_MEETING = "UPCOMING_MEETING",
}

const SUGGESTION_LABEL = {
	[SuggestionReason.RECURRING_ONE_ON_ONE]: "Recurring 1-on-1",
	[SuggestionReason.UPCOMING_ONE_ON_ONE]: "Upcoming chat",
	[SuggestionReason.RECURRING_MEETING]: "Recurring team meeting",
	[SuggestionReason.UPCOMING_MEETING]: "Upcoming team meeting",
};

function ContactSuggestion({
	contact,
	analytics,
	onSelectContact,
}: {
	contact: SimpleContact & { suggestedReason: SuggestionReason | null };
	analytics: object;
	onSelectContact: (contact: SimpleContact) => void;
}) {
	const [cursored, setCursored] = React.useState(false);

	const onSuggestionClick = React.useCallback(() => {
		dispatchAnalyticsTrack("[Invite Dialog] Suggestion clicked", analytics);
		onSelectContact(contact);
	}, [contact, onSelectContact, analytics]);

	const onFocus = React.useCallback(() => {
		setCursored(true);
	}, [setCursored]);

	const onBlur = React.useCallback(() => {
		setCursored(false);
	}, []);

	return (
		<KeyboardNavigation.Focusable onFocus={onFocus} onBlur={onBlur} onEnterDown={onSuggestionClick}>
			<div
				className={cx(styles.suggestion, {
					[styles.cursored]: cursored,
				})}
				onClick={onSuggestionClick}
			>
				<div style={{ display: "flex" }}>
					<Avatar user={contact} link={false} className={styles.suggestionAvatar} />
					<div className={styles.suggestionTitle}>
						<div className={styles.suggestionName}>
							<span className={styles.suggestionNameText}>{contact.fullName}</span>
							{contact.suggestedReason && (
								<div className={styles.suggestionReason}>{SUGGESTION_LABEL[contact.suggestedReason]}</div>
							)}
						</div>
						<div className={styles.suggestionEmail}>{contact.email}</div>
					</div>
				</div>
				<FontAwesomeIcon icon={contact.isGroup ? faUsers : faUserPlus} size="sm" />
			</div>
		</KeyboardNavigation.Focusable>
	);
}

interface ContactSearchStepProps {
	contacts: Contact[];
	inviting: boolean;
	analytics: object;
	excludeConfigured?: boolean;
	excludeExternal?: boolean;
	excludeInactive?: boolean;
	setContacts: (contacts: Contact[]) => void;
	onNext: () => void;
}

export function ContactSearchStep({
	contacts,
	inviting,
	analytics,
	excludeConfigured = false,
	excludeExternal = false,
	excludeInactive = false,
	setContacts,
	onNext,
}: ContactSearchStepProps) {
	const vars = React.useMemo(
		() => ({
			excludeConfigured,
			excludeExternal,
			excludeInactive,
			start: moment().startOf("day").format(),
			end: moment().startOf("day").add(2, "weeks").format(),
		}),
		[excludeConfigured, excludeExternal, excludeInactive],
	);

	const { viewer } = useLazyLoadQuery<GenericInviteQuery>(
		graphql`
			query GenericInviteQuery(
				$start: DateTime!
				$end: DateTime!
				$excludeConfigured: Boolean!
				$excludeExternal: Boolean!
				$excludeInactive: Boolean!
			) {
				viewer {
					id
					personalInviteLink {
						id
						isEnabled
						inviteLink
					}
					events(start: $start, end: $end) {
						edges {
							node {
								id
								type
								start
								recurrenceRule
								isOneOnOne
								attendees {
									email
								}
							}
						}
					}
					foundContacts: contacts(
						ranked: true
						search: ""
						excludeConfigured: $excludeConfigured
						excludeExternal: $excludeExternal
						excludeInactive: $excludeInactive
						excludeGroups: true
					) {
						edges {
							node {
								...ContactSearch_contact @relay(mask: false)
							}
						}
					}
				}
			}
		`,
		vars,
		{ fetchPolicy: "network-only" },
	);

	const { foundContacts } = viewer;

	const onSelectContact = React.useCallback(
		(contact: SimpleContact) => {
			const user = {
				...contact,
				sendInviteEmail: !contact.user?.isConfigured,
				relationship: (contact.isGroup ? "TEAMMATE" : "SUBORDINATE") as Relationship,
			};

			setContacts(xorBy(contacts, [user], "email"));
		},
		[contacts, setContacts],
	);

	const dropLastContact = React.useCallback(() => {
		setContacts(slice(contacts, 0, -1));
	}, [contacts, setContacts]);

	const events = React.useMemo(() => reduceEdgesToNodes(viewer.events.edges), [viewer.events.edges]);
	const nodes = React.useMemo(
		() =>
			foundContacts
				? reduceEdgesToNodes(foundContacts.edges)
						.filter(contact => !find(contacts, { id: contact.id }))
						.slice(0, CONTACTS_PER_PAGE)
						.map(contact => {
							const commonEvents = events.filter(event =>
								event.attendees.some(attendee => attendee?.email === contact.email),
							);
							const oneOneOnOneEvents = commonEvents.filter(event => event.isOneOnOne);
							if (oneOneOnOneEvents.some(event => event.recurrenceRule))
								return { ...contact, suggestedReason: SuggestionReason.RECURRING_ONE_ON_ONE };
							if (oneOneOnOneEvents.length)
								return { ...contact, suggestedReason: SuggestionReason.UPCOMING_ONE_ON_ONE };
							if (commonEvents.some(event => event.recurrenceRule))
								return { ...contact, suggestedReason: SuggestionReason.RECURRING_MEETING };
							if (commonEvents.length) return { ...contact, suggestedReason: SuggestionReason.UPCOMING_MEETING };
							return { ...contact, suggestedReason: null };
						})
				: [],
		[foundContacts, events, contacts],
	);

	return (
		<>
			<div className={styles.subtitle}>Add by email</div>

			<div className={styles.contactSearch}>
				<ContactSearch
					key="member-select"
					excludeConfigured={excludeConfigured}
					excludeExternal={excludeExternal}
					excludeInactive={excludeInactive}
					selection={contacts}
					onEmptyBackspace={dropLastContact}
					onSelection={onSelectContact}
					filter={identity}
					first={CONTACTS_PER_PAGE}
				/>
				<Button
					size="normal"
					variant="primary"
					onClick={onNext}
					disabled={!contacts.length || inviting}
					data-test-id="generic-invite-contacts-next"
					className={styles.contactNext}
				>
					Next
				</Button>
			</div>

			<div className={styles.subtext}>Search by name or type an email address</div>

			{nodes.length > 0 && (
				<div>
					<div className={styles.suggestionsTitle}>
						<span className={styles.subtitle}>Suggested teammates</span>
					</div>
					<div className={styles.suggestions}>
						<KeyboardNavigation>
							{nodes.map(node => (
								<ContactSuggestion
									key={node.id}
									contact={node}
									onSelectContact={onSelectContact}
									analytics={analytics}
								/>
							))}
						</KeyboardNavigation>
					</div>
				</div>
			)}

			<div className={styles.invitationWarning}>
				<FontAwesomeIcon icon={faUserPlus} />
				<p>New teammates will be invited to join Fellow.</p>
			</div>
		</>
	);
}

export interface GenericInviteProps extends RouteComponentProps {
	relationship?: Relationship;
	analytics: { [key: string]: string };
	onClose?: (data?: { invitees?: Invitees; inviteSent: boolean }) => void;
}

export default withRouter(function GenericInvite({ onClose, ...props }: GenericInviteProps) {
	const [step, setStep] = React.useState<Step>("contacts");
	const [contacts, setContacts] = React.useState<Contact[]>([]);
	const [inviting, setInviting] = React.useState(false);

	/* eslint-disable camelcase */
	const analytics = React.useMemo(
		() => ({
			tracking_category: "ux__web",
			invite_type: "generic",
			...props.analytics,
		}),
		[props.analytics],
	);
	/* eslint-enable camelcase */

	const closeModal = React.useCallback(() => {
		onClose?.();

		setStep("contacts");
		setContacts([]);

		// eslint-disable-next-line camelcase
		dispatchAnalyticsTrack("[Invite Dialog] Closed", analytics);
	}, [analytics, onClose]);

	React.useEffect(() => {
		window.hj?.("trigger", "invitedialog_opened");
		// eslint-disable-next-line camelcase
		dispatchAnalyticsTrack("[Invite Dialog] Opened", analytics);
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const displayError = React.useCallback((msg: string, { reset = false, autoClose = 5000 } = {}) => {
		toast.error(
			<div>
				<strong>Error:</strong> Something went wrong inviting your teammates.
				<br />
				<strong>{msg}</strong>
			</div>,
			{ autoClose },
		);
		if (reset) setContacts([]);
	}, []);

	const inviteMultipleContacts = React.useCallback(async () => {
		setInviting(true);

		const captchaToken = await recaptcha("generic_invite");
		const invitations = contacts.map(contact => ({
			...pick(contact, ["email", "fullName", "relationship", "sendInviteEmail"]),
		}));

		// eslint-disable-next-line camelcase
		dispatchAnalyticsTrack("[Invite Dialog] Invited", { num_invites: invitations.length, ...analytics });

		commitMutation<GenericInviteUsersMutation>(environment, {
			mutation: InviteUsersMutation,
			variables: {
				invite: {
					invitations,
					captchaToken,
					analytics: JSON.stringify(analytics),
				},
			},
			onCompleted: ({ inviteUsers }, err) => {
				setInviting(false);

				if (err) return displayError(err[0].message);
				if (inviteUsers) {
					const { invitees } = inviteUsers;
					invitees.forEach(({ errors }) => errors?.[0] && displayError(errors[0].message, { autoClose: undefined }));

					const successful = invitees.filter(({ errors }) => !errors?.length);
					if (successful.length) onClose?.({ invitees: successful, inviteSent: true });
				}
				closeModal();
			},
		});
	}, [contacts, analytics, displayError, closeModal, onClose]);

	const stepToRelationships = React.useCallback(() => {
		if (contacts.some(contact => !contact.user || !contact.user.isConfigured)) {
			setStep("relationships");
			dispatchAnalyticsTrack("[Invite Dialog] Step to relationships", analytics);
		} else {
			inviteMultipleContacts();
		}
	}, [contacts, inviteMultipleContacts, analytics]);

	const backToContacts = React.useCallback(() => {
		setStep("contacts");
		dispatchAnalyticsTrack("[Invite Dialog] Step back to contacts", analytics);
	}, [analytics]);

	const onRelationshipChange = React.useCallback(
		(contact: Contact, value: Relationship) => {
			contact.relationship = value;
			setContacts(unionBy([contact], contacts, "id"));
		},
		[contacts],
	);

	function renderContactSearch() {
		return (
			<ContactSearchStep
				excludeConfigured
				excludeExternal
				excludeInactive
				contacts={contacts}
				inviting={inviting}
				analytics={analytics}
				setContacts={setContacts}
				onNext={stepToRelationships}
			/>
		);
	}

	function renderRelationships() {
		return (
			<div className={styles.contacts}>
				{contacts.map(contact => (
					<InviteContact key={contact.id} contact={contact} onRelationshipChange={onRelationshipChange} />
				))}
			</div>
		);
	}

	const child = (function () {
		if (contacts.length > 0 && step == "relationships") return renderRelationships;
		return renderContactSearch;
	})();

	return (
		<Modal onClose={closeModal} width="27.5rem">
			<Modal.Header>
				<Modal.Title>{step === "relationships" ? "Set your relationships" : "Add your teammates"}</Modal.Title>
				<Modal.HeaderActions onClose={closeModal}>
					<div
						className={styles.helpIcon}
						{...setTooltip(
							// eslint-disable-next-line max-len
							"Invite your manager, direct reports, mentor, coaches or other teammates to join your workspace on Fellow.",
							{
								inverted: true,
								position: "top center",
								width: "256px",
							},
						)}
					>
						<FontAwesomeIcon icon={faQuestionCircle} />
					</div>
				</Modal.HeaderActions>
			</Modal.Header>
			<Modal.Body className={styles.inviteContainer} data-test-id="invite-container">
				<React.Suspense
					fallback={
						<div className={styles.loaderContainer}>
							<LoadingSpinner inline className={styles.loader} />
						</div>
					}
				>
					{child()}
				</React.Suspense>
			</Modal.Body>
			{step === "relationships" && (
				<Modal.Footer>
					<Button size="normal" onClick={backToContacts} disabled={inviting}>
						Back
					</Button>
					<Button
						size="normal"
						variant="primary"
						onClick={inviteMultipleContacts}
						disabled={inviting}
						data-test-id="generic-invite-relationships-next"
					>
						{inviting ? <FontAwesomeIcon icon={faSpinnerThird} spin /> : "Send invites"}
					</Button>
				</Modal.Footer>
			)}
		</Modal>
	);
});
